import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import { is } from 'ramda'
import { PARTNER_APP_INTEGRATION_STATUSES } from '@/lib/consts.js'
import { COMPANIES_WITH_DISABLED_BUTTON } from './consts'
import { useCore } from '@/composables/core'

type PartnerApp = {
  application_client_id: string
  status?: string
  company_name?: string
  application_config?: {
    call_to_actions?: Record<string, string>
  }
}

type BusinessWithPartners = {
  partner_apps_enabled?: boolean
  partner_apps?: PartnerApp[]
}

export type UsePartnerAppsReturn = {
  PARTNER_APP_INTEGRATION_STATUSES: typeof PARTNER_APP_INTEGRATION_STATUSES
  isPartnerAppsEnabled: ComputedRef<boolean>
  isPartnerEnabled: (partnerType: string) => boolean
  getPartner: (partnerType: string) => PartnerApp | undefined
  getPartnerStatus: (partnerType: string) => string | undefined
  getPartnerStatusLabel: (partnerType: string) => string
  isPartnerSuspended: (partnerType: string) => boolean
  isPartnerBlocked: (partnerType: string) => boolean
  isPartnerDisabled: (partnerType: string) => boolean
  getCallToActionLabel: (partnerType: string, callToActionName: string) => string | undefined
  partnerAppAction: (partnerType: string, callToActionName: string, callToActionId: string, showSettingsMode: boolean) => void
  canPartnerAppBeDisabled: (partnerType: string) => boolean
  buySubscriptionRedirect: () => void
}

export function usePartnerApps (): UsePartnerAppsReturn {
  const { t, router, store } = useCore()

  const business = (store.state as { business: { business: BusinessWithPartners } }).business.business

  const isPartnerAppsEnabled = computed(() => {
    return !!business.partner_apps_enabled
  })

  const getPartner = (partnerType: string): PartnerApp | undefined => {
    return business.partner_apps?.find(p => p.application_client_id === partnerType)
  }

  const getPartnerStatus = (partnerType: string): string | undefined => {
    const partner = getPartner(partnerType)
    return partner?.status
  }

  const isPartnerEnabled = (partnerType: string): boolean => {
    return !!isPartnerAppsEnabled.value && is(Object, getPartner(partnerType))
  }

  const isPartnerStatus = (partnerType: string, status: string): boolean => {
    if (!isPartnerAppsEnabled.value) return false
    return getPartnerStatus(partnerType) === status
  }

  const isPartnerSuspended = (partnerType: string): boolean => {
    return isPartnerStatus(partnerType, PARTNER_APP_INTEGRATION_STATUSES.SUSPENDED)
  }

  const isPartnerBlocked = (partnerType: string): boolean => {
    return isPartnerStatus(partnerType, PARTNER_APP_INTEGRATION_STATUSES.BLOCKED)
  }

  const isPartnerDisabled = (partnerType: string): boolean => {
    return isPartnerStatus(partnerType, PARTNER_APP_INTEGRATION_STATUSES.DISABLED)
  }

  const canPartnerAppBeDisabled = (partnerType: string): boolean => {
    const partner = getPartner(partnerType)
    const isDisabled = !!COMPANIES_WITH_DISABLED_BUTTON.find((elem) => {
      return elem === partner?.company_name
    })

    return !isDisabled
  }

  const getPartnerStatusLabel = (partnerType: string): string => {
    const status = getPartnerStatus(partnerType)
    if (status === PARTNER_APP_INTEGRATION_STATUSES.ACTIVE) {
      return t('views.business.partner-app.status.active')
    }
    if (status === PARTNER_APP_INTEGRATION_STATUSES.BLOCKED) {
      return t('views.business.partner-app.status.blocked')
    }
    if (status === PARTNER_APP_INTEGRATION_STATUSES.PAID) {
      return t('views.business.partner-app.status.paid')
    }
    if (status === PARTNER_APP_INTEGRATION_STATUSES.SUSPENDED) {
      return t('views.business.partner-app.status.suspended')
    }
    if (status === PARTNER_APP_INTEGRATION_STATUSES.DISABLED) {
      return t('views.business.partner-app.status.disabled')
    }
    return ''
  }

  const getCallToActionLabel = (partnerType: string, callToActionName: string): string | undefined => {
    const partner = getPartner(partnerType)
    return partner?.application_config?.call_to_actions?.[callToActionName]
  }

  const partnerAppAction = (
    partnerType: string,
    callToActionName: string,
    callToActionId: string,
    showSettingsMode: boolean
  ): void => {
    router.push({
      name: showSettingsMode ? 'settings-partner-app' : 'partner-app',
      params: {
        partnerType,
        callToActionName,
        callToActionId,
      },
    })
  }

  const buySubscriptionRedirect = (): void => {
    router.push({
      name: 'settings.subscription.your-subscription',
    })
  }

  return {
    PARTNER_APP_INTEGRATION_STATUSES,
    isPartnerAppsEnabled,
    isPartnerEnabled,
    getPartner,
    getPartnerStatus,
    getPartnerStatusLabel,
    isPartnerSuspended,
    isPartnerBlocked,
    isPartnerDisabled,
    getCallToActionLabel,
    partnerAppAction,
    canPartnerAppBeDisabled,
    buySubscriptionRedirect,
  }
}
